import { Component, mixins, namespace, Prop } from 'nuxt-property-decorator'
import {
  IParam,
  IProductAdditionalService,
  IProductToCompare,
  IProductToCart,
  IProductToFavourites,
  ITag,
  IVariant,
} from '~/assets/interfaces/products'
import { ICartItem } from '~/assets/interfaces/cart'
import MixinGeneral from '~/assets/mixins/general'

type LoadingFlags = 'toCartMinus' | 'toCartPlus' | 'toFavourites' | 'toCompare'

const cartStore = namespace('cart')
const favouritesStore = namespace('favourites')
const compareStore = namespace('compare')

@Component({})
export default class MixinProduct extends mixins(MixinGeneral) {
  @Prop({ type: String }) private url!: string
  @Prop({ type: Number }) private id!: number
  @Prop({ type: String }) private title!: string
  @Prop({ type: String, default: null }) public readonly unit!: string
  @Prop({ type: Number, default: 1 }) public readonly multiplicity!: number
  @Prop({ type: Number }) private price!: number
  @Prop({ type: Number }) private finalPrice!: number
  @Prop({ type: Number, default: 0 }) private rating!: number
  @Prop({ type: Number, default: 0 }) private reviews!: number
  @Prop({ type: Array, default: () => [] }) private tags!: ITag[]
  @Prop({ type: Array, default: () => [] }) private variants!: IVariant[]
  @Prop({ type: Array, default: () => [] }) private params!: IParam[]
  @Prop({ type: Array, default: () => [] })
  readonly aspectsForBuy!: IProductToCart[]

  @Prop({ type: Array, default: () => [] })
  readonly servicesForBuy!: IProductAdditionalService[]

  @cartStore.Action('toCart') public actionToCart!: (
    product: IProductToCart | IProductToCart[]
  ) => Promise<void>

  @cartStore.State('items') private cartItems!: ICartItem[]
  @favouritesStore.Action('toFavourites') public actionToFavourites!: (
    product: IProductToFavourites
  ) => Promise<void>

  @favouritesStore.State('items')
  private favouritesItems!: IProductToFavourites[]

  @compareStore.Action('toCompare') public actionToCompare!: (
    product: IProductToCompare
  ) => Promise<void>

  @compareStore.State('items') private compareItems!: IProductToCompare[]

  private isLoading: Record<LoadingFlags, boolean> = {
    toCartMinus: false,
    toCartPlus: false,
    toFavourites: false,
    toCompare: false,
  }

  private getProductInArray<T extends { id: number }>(
    array: T[]
  ): T | undefined {
    return array.find((item) => item.id === this.id)
  }

  private handleAction(
    action: (product: { id: number }) => Promise<void>,
    isLoadingFlag: LoadingFlags
  ) {
    return async () => {
      if (!this.isLoading[isLoadingFlag]) {
        this.startLoading(isLoadingFlag)
        await action({ id: this.id })
        this.stopLoading(isLoadingFlag)
      }
    }
  }

  private startLoading(flag: LoadingFlags) {
    this.isLoading[flag] = true
  }

  private stopLoading(flag: LoadingFlags) {
    this.isLoading[flag] = false
  }

  public get isLoadingToCart() {
    return this.isLoading.toCartMinus || this.isLoading.toCartPlus
  }

  private get productInCart() {
    return this.getProductInArray(this.cartItems)
  }

  public get isInCart() {
    return !!this.productInCart
  }

  public get currentQuantity() {
    if (this.productInCart) {
      return this.productInCart.quantity || 0
    }
    return 0
  }

  public get isInFavourites() {
    return !!this.getProductInArray(this.favouritesItems)
  }

  public get buttonFavouritesText() {
    return this.isInFavourites
      ? 'Удалить из избранного'
      : 'Добавить в избранное'
  }

  public get isInCompare() {
    return !!this.getProductInArray(this.compareItems)
  }

  public get buttonCompareText() {
    return this.isInCompare ? 'Удалить из сравнения' : 'Добавить в сравнение'
  }

  public get buttonBuyText() {
    return this.isInCart && this.aspectsForBuy.length === 0
      ? 'В корзине'
      : 'В корзину'
  }

  private get isShowTags() {
    return this.tags.length > 0
  }

  private get isShowVariants() {
    return this.variants.length > 0
  }

  private get isShowParams() {
    return this.params.length > 0
  }

  private createProductData(
    quantity: number,
    services: IProductAdditionalService[]
  ) {
    const tempServicesForBuy = services
      .filter((item) => item.active)
      .map((item) => ({ id: item.id }))
    if (this.aspectsForBuy.length > 0) {
      return this.aspectsForBuy.map((item) => ({
        ...item,
        services: tempServicesForBuy,
      }))
    } else {
      return { id: this.id, quantity, services: tempServicesForBuy }
    }
  }

  private async handleCartAction(
    quantity: number,
    servicesForBuy: IProductAdditionalService[]
  ) {
    if (!this.isLoading.toCartMinus && !this.isLoading.toCartPlus) {
      this.isLoading.toCartMinus = quantity <= this.currentQuantity
      this.isLoading.toCartPlus = !this.isLoading.toCartMinus
      const productData = this.createProductData(quantity, servicesForBuy)
      await this.actionToCart(productData)
      this.isLoading.toCartMinus = false
      this.isLoading.toCartPlus = false
    }
  }

  public async toCart(quantity: number = 1) {
    await this.$nextTick()
    await this.handleCartAction(quantity, this.servicesForBuy)
  }

  public toFavourites = this.handleAction(
    this.actionToFavourites,
    'toFavourites'
  )

  public toCompare = this.handleAction(this.actionToCompare, 'toCompare')
}
